import { useCallback, useState } from 'react';
import { Button, Modal, ModalProps } from 'react-bootstrap';
import classNames from 'classnames';
import { createUseThemedStyles, useHandleError } from '@/hooks';
import starEmpty from '@/assets/icons/rating-star-empty.svg';
import starFilled from '@/assets/icons/rating-star-filled.svg';
import { DocumentModel } from '@/lib/models';
import { blogsService } from '@/lib/services';
// import { blogsService } from '@/lib/services';

const useStyles = createUseThemedStyles((theme) => ({
	dialog: {
		width: '90%',
		height: '100%',
		maxWidth: 610,
		margin: '0 auto',
		'& .modal-content': {
			maxHeight: '90vh',
		},
		'& .hackett-modal__body': {
			overflowY: 'auto',
		},
	},
	star: {
		width: 90,
		height: 80,
		padding: 0,
		maskSize: 80,
		maskPosition: 'center',
		maskRepeat: 'no-repeat',
		maskImage: `url(${starEmpty})`,
		backgroundColor: theme.colors.gray500,
		'&:hover, &:has(~ button:hover)': {
			maskImage: `url(${starFilled})`,
			backgroundColor: theme.colors.primary,
		},
		'&.rated': {
			maskImage: `url(${starFilled})`,
			backgroundColor: theme.colors.primary,
		},
	},
}));

interface ConfirmDialogProps extends ModalProps {
	blog: DocumentModel;
	onSubmit(blog: DocumentModel): void;
	initialRating?: number;
}

export const BlogRateModel = ({ blog, onSubmit, initialRating, ...props }: ConfirmDialogProps) => {
	const classes = useStyles();
	const handleError = useHandleError();
	const [isLoading, setIsLoading] = useState(false);
	const [rating, setRating] = useState(0);

	const handleOnEnter = useCallback(() => {
		setRating(initialRating ?? 0);
	}, [initialRating]);

	const onSubmitButtonClick = useCallback(async () => {
		setIsLoading(true);

		try {
			const response = await blogsService.rateBlog(blog.documentId, { rating }).fetch();
			onSubmit(response.blog);
		} catch (error) {
			handleError(error);
		} finally {
			setIsLoading(false);
		}
	}, [blog.documentId, handleError, onSubmit, rating]);

	return (
		<Modal dialogClassName={classes.dialog} centered onEnter={handleOnEnter} {...props}>
			<Modal.Header closeButton>
				<Modal.Title>Rate Blog Post</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<p>Please rate the content of this post.</p>
				<div className="py-8 d-flex justify-content-center align-items-center">
					{Array.from({ length: 5 }, (_, index) => index).map((index) => (
						<Button
							key={index}
							bsPrefix="blog-rate-star"
							className={classNames(classes.star, {
								rated: index < rating,
							})}
							onClick={() => {
								setRating(index + 1);
							}}
							disabled={isLoading}
						/>
					))}
				</div>
			</Modal.Body>
			<Modal.Footer>
				<div className="text-right">
					<Button
						type="button"
						className="mr-2"
						variant="outline-primary"
						onClick={props.onHide}
						disabled={isLoading}
					>
						Cancel
					</Button>
					<Button type="button" onClick={onSubmitButtonClick} disabled={isLoading}>
						Submit
					</Button>
				</div>
			</Modal.Footer>
		</Modal>
	);
};
